import React, { useState } from 'react';
import { Button } from './ui/button';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const ImageUpload = ({ onImageUploaded, multiple = false, maxFiles = 5, currentImages = [] }) => {
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState(currentImages);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    
    if (multiple && files.length > maxFiles) {
      toast.error(`Maximum ${maxFiles} images autorisées`);
      return;
    }

    setUploading(true);

    try {
      if (multiple) {
        const formData = new FormData();
        files.forEach(file => formData.append('files', file));

        const response = await axios.post(`${API}/upload/product-images`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        const newImages = response.data.urls.map(url => ({ url, is_primary: images.length === 0 }));
        const updatedImages = [...images, ...newImages];
        setImages(updatedImages);
        onImageUploaded(updatedImages);
        toast.success(`${files.length} image(s) uploadée(s)`);
      } else {
        const formData = new FormData();
        formData.append('file', files[0]);

        const response = await axios.post(`${API}/upload/product-image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        const newImage = { url: response.data.url, is_primary: true };
        setImages([newImage]);
        onImageUploaded([newImage]);
        toast.success('Image uploadée');
      }
    } catch (error) {
      console.error('Error uploading:', error);
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = (index) => {
    const updatedImages = images.filter((_, i) => i !== index);
    setImages(updatedImages);
    onImageUploaded(updatedImages);
  };

  const handleSetPrimary = (index) => {
    const updatedImages = images.map((img, i) => ({
      ...img,
      is_primary: i === index
    }));
    setImages(updatedImages);
    onImageUploaded(updatedImages);
  };

  return (
    <div className="space-y-4">
      <div>
        <input
          type="file"
          id="image-upload"
          accept="image/*"
          multiple={multiple}
          onChange={handleFileSelect}
          className="hidden"
          disabled={uploading}
        />
        <label htmlFor="image-upload">
          <Button
            type="button"
            variant="outline"
            disabled={uploading}
            onClick={() => document.getElementById('image-upload').click()}
            className="w-full"
          >
            <Upload className="w-4 h-4 mr-2" />
            {uploading ? 'Upload en cours...' : multiple ? 'Upload Images (max ' + maxFiles + ')' : 'Upload Image'}
          </Button>
        </label>
      </div>

      {images.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {images.map((image, index) => (
            <div key={index} className="relative group">
              <img
                src={`${BACKEND_URL}${image.url}`}
                alt={`Upload ${index + 1}`}
                className="w-full h-32 object-cover rounded-lg border-2 border-gray-200"
              />
              {image.is_primary && (
                <div className="absolute top-2 left-2 bg-blue-600 text-white text-xs px-2 py-1 rounded">
                  Principale
                </div>
              )}
              <div className="absolute top-2 right-2 space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {multiple && !image.is_primary && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => handleSetPrimary(index)}
                    className="h-8 w-8 p-0"
                  >
                    <ImageIcon className="w-4 h-4" />
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleRemoveImage(index)}
                  className="h-8 w-8 p-0"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
